import {
  InlineSelectValue,
  inlineSelectTriggerClasses
} from "@/components/ui/InlineSelectTrigger";
import { useReportLoadFailure } from "@/hooks/useReportLoadFailure";
import { handleActionError, RETRY_HINT } from "@/lib/errors";
import {
  useChangeCartItemVariant,
  useLineItemVariantOptions
} from "@/queries/cart";
import { resolveLineItemDisplay } from "@/utils/cart";
import { convertToLocale } from "@/utils/money";
import { Select, toast } from "@medusajs/ui";
import type { SalonStockerHttpTypes } from "@repo/api-sdk";
import { cn } from "@repo/design-system/utils";
import { Loader2 } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import type { CartLineItemProps } from ".";

export interface VariantPickerProps {
  cartId: string;
  lineItemId: string;
  /** The line's product name, so option titles dedupe against it. */
  productTitle: string;
  /**
   * What the line already prints as its variant - the trigger's text. `null`
   * when the line prints none (a placeholder variant, or one that restates the
   * product), in which case the trigger falls back to the current option's
   * label, then `currentSku`, then a neutral prompt.
   */
  currentLabel: string | null;
  currentSku?: string | null;
  /**
   * What the line is on - its offer, or for an ad hoc line its Shopify
   * variant. A second check beside the options' own `is_current`, for a
   * response that flags none.
   */
  lineChoice?: VariantChoice;
  size?: CartLineItemProps["size"];
  /**
   * Awaited before the switch is sent. The row passes the quantity flush here,
   * so a debounced edit lands on the line before the server reads it.
   */
  onBeforeChange?: () => Promise<void>;
  /**
   * Reports the switch being under way - from the moment an option is chosen,
   * through the flush and the request, until the refreshed cart has landed - so
   * the row can lock around all of it.
   */
  onPendingChange?: (isPending: boolean) => void;
}

const NEUTRAL_LABEL = "Choose variant";

type VariantOption = SalonStockerHttpTypes.StoreCartLineVariantOption;
export type VariantChoice = SalonStockerHttpTypes.StoreChangeLineItemVariant;

/**
 * What identifies an option on the wire: the offer for an offer line, the
 * Shopify variant for an ad hoc line (whose options have no offer). `null` for
 * an option carrying neither, which cannot be chosen.
 *
 * Decided from the option's own shape, not from the line's kind: the backend
 * contract guarantees `offer_id` is null on every ad hoc option and set on
 * every offer option, so the shape is the contract.
 */
function choiceOf(option: VariantOption): VariantChoice | null {
  if (option.offer_id !== null) return { offer_id: option.offer_id };
  if (option.shopify_variant_id !== null) {
    return { shopify_variant_id: option.shopify_variant_id };
  }
  return null;
}

/**
 * The Select's value for a choice. Prefixed by kind so an offer id and a
 * Shopify variant id can never be mistaken for one another.
 */
function choiceKey(choice: VariantChoice): string {
  return choice.offer_id !== undefined
    ? `offer:${choice.offer_id}`
    : `shopify:${choice.shopify_variant_id}`;
}

/**
 * The label for one option. Reuses the line's own dedupe so a variant that
 * merely restates the product is not printed, and falls back to the option
 * values, then the SKU, for suppliers whose variant title is a placeholder.
 */
function optionLabel(
  option: SalonStockerHttpTypes.StoreCartLineVariantOption,
  productTitle: string
): string {
  const { variantTitle } = resolveLineItemDisplay({
    title: productTitle,
    product_title: productTitle,
    variant_title: option.title
  });

  return (
    variantTitle ??
    (option.options.map((o) => o.value).join(" / ") ||
      option.sku ||
      option.title)
  );
}

/**
 * Lets a salon switch a cart line to another variant of the same product from
 * the same supplier (e.g. a different size or shade) without removing the line
 * and searching for it again.
 *
 * Takes the place of the plain variant line, styled to read as that line until
 * it is hovered or focused. The option list is fetched only when the picker is
 * first opened. The mutation owns the reconciliation; this only reports
 * failures, since the cart has not moved if it failed.
 */
export function VariantPicker({
  cartId,
  lineItemId,
  productTitle,
  currentLabel,
  currentSku,
  lineChoice,
  size = "md",
  onBeforeChange,
  onPendingChange
}: VariantPickerProps) {
  const [open, setOpen] = useState(false);
  // Set before the quantity flush, not when the request goes out: the flush can
  // take a moment, and a second choice or a quantity tap inside that window
  // would otherwise slip past the mutation's own pending flag.
  const [isSwitching, setIsSwitching] = useState(false);
  const switching = useRef(false);

  const options = useLineItemVariantOptions(cartId, lineItemId, {
    enabled: open
  });
  const { mutateAsync: changeVariant } = useChangeCartItemVariant(cartId);

  useReportLoadFailure(options.error, "cart variant options");

  useEffect(() => {
    onPendingChange?.(isSwitching);
    // Also on unmount: the refetched cart can drop the flag from this line
    // (or remove the line) while a switch is under way, and the row must not be
    // left locked by a picker that is no longer there to unlock it.
    return () => onPendingChange?.(false);
  }, [isSwitching, onPendingChange]);

  const variantOptions = options.data?.variant_options;
  // Every option that can be identified, with its Select value, built once.
  const { entries, choices } = useMemo(() => {
    const all = (variantOptions ?? []).flatMap((option) => {
      const choice = choiceOf(option);
      return choice ? [{ option, choice, key: choiceKey(choice) }] : [];
    });
    return {
      entries: all,
      // Only options that can be chosen: handleChange can never send an
      // unavailable one, and an unavailable current option is never the value.
      choices: new Map(
        all
          .filter((entry) => entry.option.available)
          .map((entry) => [entry.key, entry.choice])
      )
    };
  }, [variantOptions]);

  const currentOption = variantOptions?.find((option) => option.is_current);
  const currentChoice =
    (currentOption && choiceOf(currentOption)) ?? lineChoice;
  const currentKey = currentChoice ? choiceKey(currentChoice) : null;
  // "" matches no Item, until the options say which one is current.
  const selectedKey =
    currentKey !== null && choices.has(currentKey) ? currentKey : "";

  const triggerLabel =
    currentLabel ??
    (currentOption && optionLabel(currentOption, productTitle)) ??
    currentSku ??
    NEUTRAL_LABEL;

  const handleChange = async (key: string) => {
    // Choosing again while a switch is under way is not a second request. (The
    // select itself never reports the variant it already shows as a change.)
    if (switching.current) return;
    const variant = choices.get(key);
    if (!variant) return;

    switching.current = true;
    setIsSwitching(true);
    try {
      await onBeforeChange?.();
      await changeVariant({ lineItemId, variant });
    } catch (error) {
      toast.error("Couldn't change the variant", {
        description: handleActionError(error, {
          boundary: "cart variant change",
          fallback: RETRY_HINT
        })
      });
    } finally {
      switching.current = false;
      setIsSwitching(false);
    }
  };

  const isSm = size === "sm";
  const triggerValue = (
    <InlineSelectValue chevron="trailing">{triggerLabel}</InlineSelectValue>
  );

  return (
    <Select
      // Controlled, "" (matching no Item) until the options say which one is
      // current: the trigger text comes from the line itself, never from an
      // Item, so it cannot flash the wrong variant while options load.
      value={selectedKey}
      onValueChange={handleChange}
      open={open}
      onOpenChange={setOpen}
      disabled={isSwitching}
    >
      <Select.Trigger
        aria-label={`Change variant of ${productTitle}. Current: ${triggerLabel}`}
        aria-busy={isSwitching}
        className={cn(
          inlineSelectTriggerClasses,
          "!max-w-full !justify-start font-normal !text-gray-500",
          isSm ? "!text-[10px]" : "!text-xs"
        )}
      >
        <Select.Value placeholder={triggerValue}>{triggerValue}</Select.Value>
      </Select.Trigger>
      <Select.Content className="z-[100]">
        {options.isPending && (
          <Select.Item value="__loading" disabled>
            <span className="flex items-center gap-2 text-gray-500">
              <Loader2 className="h-3 w-3 animate-spin" aria-hidden="true" />
              Loading variants...
            </span>
          </Select.Item>
        )}
        {options.isError && (
          <Select.Item value="__error" disabled>
            <span role="alert" className="text-red-600">
              Couldn&apos;t load variants. Close and try again.
            </span>
          </Select.Item>
        )}
        {entries.map(({ option, key }) => (
          // Radix renders a div, so the kit's `disabled:` variants never match;
          // `data-disabled` is what marks an unavailable option.
          <Select.Item
            key={key}
            value={key}
            disabled={!option.available}
            className="data-[disabled]:text-ui-fg-disabled data-[disabled]:cursor-not-allowed"
          >
            <span className="flex w-full items-baseline justify-between gap-4">
              <span>{optionLabel(option, productTitle)}</span>
              {option.unit_price !== null && (
                <span
                  className={
                    option.available ? "text-gray-500" : "text-ui-fg-disabled"
                  }
                >
                  {convertToLocale({
                    amount: option.unit_price,
                    currency_code: option.currency_code
                  })}
                </span>
              )}
            </span>
          </Select.Item>
        ))}
      </Select.Content>
    </Select>
  );
}
